import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import {
  AlertCircle,
  Briefcase,
  MoreVertical,
  Pencil,
  Plus,
  TrendingUp,
  Users,
} from "lucide-react";
import { Link } from "@tanstack/react-router";
import PageHeader from "@/components/shared/PageHeader";
import SectionCard from "@/components/shared/SectionCard";
import StatCard from "@/components/shared/StatCard";
import StatusChip from "@/components/shared/StatusChip";
import EmptyState from "@/components/shared/EmptyState";
import { IconAction } from "@/components/shared/IconAction";
import { SearchInput } from "@/components/shared/SearchInput";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  usePartnerSlots,
  usePartnerPlacementRequests,
  useAcceptPlacementRequest,
  useDeclinePlacementRequest,
  type PartnerPlacementRequest,
} from "@/hooks/usePartnerData";
import { TableSkeleton } from "@/components/shared/Skeletons";

export const Route = createFileRoute("/partner/slots/")({ component: PartnerSlots });

type PartnerSlot = {
  id: string | number;
  title?: string;
  description?: string;
  location?: string;
  requirements?: string;
  duration?: string;
  status?: string;
  max_students?: number;
  maxStudents?: number;
};

function PartnerSlots() {
  const [search, setSearch] = useState("");
  const { data: slots = [], isLoading } = usePartnerSlots();

  const filteredSlots = slots.filter(
    (s: PartnerSlot) =>
      (s.title || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.description || "").toLowerCase().includes(search.toLowerCase()) ||
      (s.location || "").toLowerCase().includes(search.toLowerCase()),
  );

  const totalPositions = slots.reduce(
    (sum: number, slot: PartnerSlot) => sum + (slot.max_students || slot.maxStudents || 0),
    0,
  );
  const activeListings = slots.filter(
    (slot: PartnerSlot) => (slot.status || "").toLowerCase() !== "closed",
  ).length;
  const closedSlots = slots.filter(
    (slot: PartnerSlot) => (slot.status || "").toLowerCase() === "closed",
  ).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="Placement openings"
        subtitle="Manage your organization's internship slots, monitor capacity, and review recruitment progress."
        breadcrumbs={[
          { label: "Dashboard", path: "/partner/dashboard" },
          { label: "Placement Slots" },
        ]}
        action={
          <Button asChild>
            <Link to="/partner/slots/new">
              <Plus />
              New placement slot
            </Link>
          </Button>
        }
      />

      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-4">
        <StatCard label="Active listings" value={activeListings} icon={<Briefcase />} />
        <StatCard label="Total positions" value={totalPositions} icon={<Users />} />
        <StatCard label="Closed slots" value={closedSlots} icon={<TrendingUp />} />
      </div>

      <SectionCard
        title="Active listings"
        actions={
          <SearchInput placeholder="Search listings..." value={search} onChange={setSearch} />
        }
        noPadding
      >
        {isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={5} columns={4} />
          </div>
        ) : filteredSlots.length === 0 ? (
          <EmptyState
            bare
            icon={<Briefcase />}
            title="No slots found"
            description="No slots match your search yet."
          />
        ) : (
          <Table className="min-w-[860px]">
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead>Role / Position</TableHead>
                <TableHead>Location</TableHead>
                <TableHead>Skill requirements</TableHead>
                <TableHead>Capacity</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">
                  <span className="sr-only">Actions</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSlots.map((s: PartnerSlot) => (
                <TableRow key={s.id}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <span
                        className="flex size-8 shrink-0 items-center justify-center rounded-sm border border-border bg-background text-body"
                        aria-hidden
                      >
                        <Briefcase className="size-4" />
                      </span>
                      <div className="min-w-0">
                        <p className="font-medium text-foreground">{s.title}</p>
                        <p className="text-caption text-muted-foreground">
                          {s.duration || "Flexible duration"}
                        </p>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="text-body">{s.location || "Not specified"}</TableCell>
                  <TableCell className="max-w-[260px]">
                    <div className="flex flex-wrap gap-1">
                      {(s.requirements || "Professional communication")
                        .split(",")
                        .map((skill: string) => skill.trim())
                        .filter(Boolean)
                        .slice(0, 4)
                        .map((skill: string) => (
                          <Badge key={skill} variant="outline">
                            {skill}
                          </Badge>
                        ))}
                    </div>
                  </TableCell>
                  <TableCell>
                    <span className="font-mono text-foreground tabular-nums">
                      {s.max_students || s.maxStudents || 0}
                    </span>{" "}
                    <span className="text-caption text-muted-foreground">slots</span>
                  </TableCell>
                  <TableCell>
                    <StatusChip status={(s.status || "published").replace("published", "open")} />
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-1">
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <Button
                            asChild
                            variant="ghost"
                            size="icon-sm"
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Link to={`/partner/slots/${s.id}` as never} aria-label="Edit posting">
                              <Pencil />
                            </Link>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent>Edit posting</TooltipContent>
                      </Tooltip>
                      <IconAction label="More actions" icon={<MoreVertical />} />
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </SectionCard>

      <ApplicationsInbox />
    </div>
  );
}

/**
 * Student applications for this partner's slots. Accepting creates the
 * placement, so the decision is made here rather than bounced to an admin.
 */
function ApplicationsInbox() {
  const { data: requests = [], isLoading } = usePartnerPlacementRequests();
  const accept = useAcceptPlacementRequest();
  const decline = useDeclinePlacementRequest();
  const [error, setError] = useState("");

  const pending = requests.filter((r) => r.status === "submitted");
  const decided = requests.filter((r) => r.status !== "submitted");

  const handleAccept = (request: PartnerPlacementRequest) => {
    setError("");
    accept.mutate(
      {
        id: request.id,
        // The API takes a unix timestamp; default to starting tomorrow so an
        // accepted student has a lead-in rather than beginning mid-shift.
        start_date: Math.floor(Date.now() / 1000) + 86400,
        hours_required: 480,
      },
      { onError: (e: any) => setError(e?.response?.data?.message || "Could not accept this application.") },
    );
  };

  const handleDecline = (request: PartnerPlacementRequest) => {
    setError("");
    decline.mutate(
      { id: request.id },
      { onError: (e: any) => setError(e?.response?.data?.message || "Could not decline this application.") },
    );
  };

  return (
    <SectionCard
      title={`Applications${pending.length ? ` (${pending.length} awaiting review)` : ""}`}
      noPadding
    >
      {error && (
        <Alert variant="destructive" className="m-4 w-auto">
          <AlertCircle />
          <AlertDescription className="text-foreground">{error}</AlertDescription>
        </Alert>
      )}

      {isLoading ? (
        <div className="p-4">
          <TableSkeleton />
        </div>
      ) : requests.length === 0 ? (
        <p className="px-4 py-6 text-sm text-muted-foreground md:px-5">
          No applications yet. Publish a slot and students can apply to it.
        </p>
      ) : (
        <Table className="min-w-[760px]">
          <TableHeader>
            <TableRow className="hover:bg-transparent">
              <TableHead>Student</TableHead>
              <TableHead>Slot</TableHead>
              <TableHead>Applied</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Decision</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {[...pending, ...decided].map((r) => (
              <TableRow key={r.id}>
                <TableCell className="max-w-[320px] whitespace-normal">
                  <p className="font-medium text-foreground">{r.student_name || "Student"}</p>
                  <p className="text-caption text-muted-foreground">
                    {[r.university, r.major].filter(Boolean).join(" • ") || r.student_email}
                  </p>
                  {r.note && (
                    <p className="mt-1 text-caption text-body italic">“{r.note}”</p>
                  )}
                </TableCell>
                <TableCell>
                  <p className="text-foreground">{r.slot_title || `Slot ${r.slot_id}`}</p>
                  {r.location && (
                    <p className="text-caption text-muted-foreground">{r.location}</p>
                  )}
                </TableCell>
                <TableCell className="text-caption text-muted-foreground tabular-nums">
                  {r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}
                </TableCell>
                <TableCell>
                  <StatusChip status={r.status === "submitted" ? "pending" : r.status} />
                </TableCell>
                <TableCell className="text-right">
                  {r.status === "submitted" ? (
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={decline.isPending}
                        onClick={() => handleDecline(r)}
                      >
                        Decline
                      </Button>
                      <Button size="sm" disabled={accept.isPending} onClick={() => handleAccept(r)}>
                        Accept
                      </Button>
                    </div>
                  ) : (
                    <span className="text-caption text-muted-foreground">
                      {r.status === "accepted" ? "Placement created" : "Closed"}
                    </span>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}
    </SectionCard>
  );
}
